import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const UrgencyAnalyzerViewResult = ({result}) => {
  const getUrgencyColor = (category) => {
    switch(category?.toLowerCase()) {
      case 'critical':
        return { bg: 'bg-error/20', text: 'text-error', icon: 'WarningCircle' };
      case 'high':
        return { bg: 'bg-warning/20', text: 'text-warning', icon: 'Warning' };
      case 'medium':
        return { bg: 'bg-info/20', text: 'text-info', icon: 'Info' };
      case 'low':
        return { bg: 'bg-success/20', text: 'text-success', icon: 'Check' };
      default:
        return { bg: 'bg-base-200', text: 'text-base-content', icon: 'Question' };
    }
  };

  const getContextClarityColor = (clarity) => {
    switch(clarity) {
      case 'high':
        return 'bg-success/20 text-success';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Urgency Level Section */}
        <div className="space-y-4">
          {/* Urgency Score Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className={`p-2 rounded-lg ${getUrgencyColor(result?.urgency_analysis?.urgency_level?.category).bg}`}>
                  {PhosphorIcons[getUrgencyColor(result?.urgency_analysis?.urgency_level?.category).icon] && 
                    React.createElement(PhosphorIcons[getUrgencyColor(result?.urgency_analysis?.urgency_level?.category).icon], {
                      size: 20,
                      weight: "bold",
                      className: getUrgencyColor(result?.urgency_analysis?.urgency_level?.category).text
                    })
                  }
                </div>
                <h3 className="card-title text-lg">Urgency Level</h3>
              </div>

              <div className="space-y-3">
                {/* Urgency Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium capitalize">
                      {result?.urgency_analysis?.urgency_level?.category} Urgency
                    </span>
                    <div className="flex gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getUrgencyColor(result?.urgency_analysis?.urgency_level?.category).bg} ${getUrgencyColor(result?.urgency_analysis?.urgency_level?.category).text}`}>
                        Score: {Math.round(result?.urgency_analysis?.urgency_level?.score * 100)}%
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                        {Math.round(result?.urgency_analysis?.urgency_level?.confidence * 100)}% confidence
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getUrgencyColor(result?.urgency_analysis?.urgency_level?.category).bg}`}
                      style={{width: `${result?.urgency_analysis?.urgency_level?.score * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Action Required */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Action Required</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      result?.urgency_analysis?.urgency_indicators?.action_required
                        ? 'bg-error/20 text-error'
                        : 'bg-success/20 text-success'
                    }`}>
                      {result?.urgency_analysis?.urgency_indicators?.action_required ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>

                {/* Response Timeframe */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Response Timeframe</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary">
                      {result?.urgency_analysis?.urgency_indicators?.response_timeframe}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Indicators and Metadata Section */}
        <div className="space-y-4">
          {/* Urgency Indicators Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.ListChecks size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Urgency Indicators</h3>
              </div>

              <div className="space-y-3">
                {/* Priority Phrases */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Priority Phrases</h4>
                  <div className="flex flex-wrap gap-2">
                    {result?.urgency_analysis?.urgency_indicators?.priority_phrases?.map((phrase, index) => (
                      <span key={index} className="px-2 py-1 bg-base-300 rounded-full text-sm">
                        {phrase}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Temporal Markers */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Time Indicators</h4>
                  <div className="flex flex-wrap gap-2">
                    {result?.urgency_analysis?.urgency_indicators?.temporal_markers?.map((marker, index) => (
                      <span key={index} className="px-2 py-1 bg-base-300 rounded-full text-sm">
                        {marker}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Assessment Quality Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.Gauge size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Assessment Quality</h3>
              </div>

              <div className="space-y-3">
                {/* Confidence Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Confidence Score</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      {Math.round(result?.metadata?.assessment_quality?.confidence_score * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-success"
                      style={{width: `${result?.metadata?.assessment_quality?.confidence_score * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Context Clarity */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Context Clarity</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getContextClarityColor(result?.metadata?.assessment_quality?.context_clarity)}`}>
                      {result?.metadata?.assessment_quality?.context_clarity}
                    </span>
                  </div>
                </div>

                {/* False Positive Risk */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">False Positive Risk</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-warning/20 text-warning">
                      {Math.round(result?.metadata?.assessment_quality?.false_positive_risk * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UrgencyAnalyzerViewResult;